import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShiftSwapRequestView } from '../../../../models/shiftSwap';
import { formatShiftDate, formatShiftTime } from './format';

interface SwapTimelineProps {
  swap: ShiftSwapRequestView;
  locale?: string;
  autoApplied: boolean;
}

type Tone = 'good' | 'bad' | 'warn' | 'neutral';

interface Step {
  key: string;
  label: string;
  ts?: string | null;
  complete: boolean;
  tone: Tone;
}

// Dot color: filled + toned when the step has happened, hollow + muted while it is still ahead.
const dotClass = (step: Step): string => {
  if (!step.complete) return 'border border-muted-foreground/40 bg-transparent';
  switch (step.tone) {
    case 'good':
      return 'bg-emerald-500';
    case 'bad':
      return 'bg-red-500';
    // Declined (a peer said no) stays orange to match StatusBadge, distinct from the red a
    // manager Rejection uses.
    case 'warn':
      return 'bg-orange-500';
    default:
      return 'bg-slate-400';
  }
};

// Compact, read-only lifecycle stepper: Requested -> Peer response -> Decision. Fed entirely
// from the view's timestamps, so it also works for terminal (history) swaps.
const SwapTimeline: React.FC<SwapTimelineProps> = ({ swap, locale, autoApplied }) => {
  const { t } = useTranslation();

  const steps: Step[] = [
    { key: 'requested', label: t('Requested'), ts: swap.createdOn, complete: true, tone: 'neutral' },
  ];

  // Peer response leg.
  if (swap.status === 'Declined') {
    steps.push({ key: 'peer', label: t('Declined'), ts: swap.counterpartyRespondedOn, complete: true, tone: 'warn' });
  } else if (swap.counterpartyRespondedOn) {
    steps.push({ key: 'peer', label: t('Peer accepted'), ts: swap.counterpartyRespondedOn, complete: true, tone: 'good' });
  } else {
    steps.push({ key: 'peer', label: t('Awaiting colleague'), ts: null, complete: false, tone: 'neutral' });
  }

  // Manager decision leg.
  if (swap.status === 'Approved') {
    steps.push({
      key: 'decision',
      label: autoApplied ? t('Applied automatically') : t('Approved'),
      ts: swap.dateOfDecision,
      complete: true,
      tone: 'good',
    });
  } else if (swap.status === 'Rejected') {
    steps.push({ key: 'decision', label: t('Rejected'), ts: swap.dateOfDecision, complete: true, tone: 'bad' });
  } else if (swap.status === 'Cancelled') {
    steps.push({ key: 'decision', label: t('Cancelled'), ts: swap.dateOfDecision, complete: true, tone: 'bad' });
  } else if (swap.status === 'Expired') {
    steps.push({ key: 'decision', label: t('Expired'), ts: swap.dateOfDecision, complete: true, tone: 'neutral' });
  } else if (swap.status === 'PeerAccepted') {
    steps.push({ key: 'decision', label: t('Awaiting approval'), ts: null, complete: false, tone: 'neutral' });
  } else {
    steps.push({ key: 'decision', label: t('Manager decision'), ts: null, complete: false, tone: 'neutral' });
  }

  return (
    <ol className="space-y-3" data-testid={`swap-timeline-${swap.id}`}>
      {steps.map((step, i) => (
        <li key={step.key} className="flex gap-3" data-testid={`swap-timeline-step-${step.key}`}>
          {/* Marker column: dot + a thin connector down to the next step. */}
          <div className="flex flex-col items-center">
            <span className={`mt-0.5 h-2.5 w-2.5 rounded-full ${dotClass(step)}`} aria-hidden="true" />
            {i < steps.length - 1 && <span className="mt-1 w-px flex-1 bg-border" aria-hidden="true" />}
          </div>
          <div className="min-w-0 pb-0.5">
            <p className={`text-sm ${step.complete ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
              {step.label}
            </p>
            {step.ts && (
              <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                {formatShiftDate(step.ts, locale)}, {formatShiftTime(step.ts, locale)}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default SwapTimeline;
